import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { HTTPError } from "ky";

import { listAssets, type Asset } from "@/api/assets";
import { getAssessment, type Assessment } from "@/api/assessments";
import {
  advanceAssessment,
  createThreat,
  deleteThreat,
  listThreats,
  reviewAssessment,
  scoreImpact,
  scoreLikelihood,
  scoreVulnerability,
  type ImpactBreakdown,
  type Threat,
} from "@/api/threats";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card, CardHeader } from "@/components/hifi/Card";
import { Icon } from "@/components/hifi/Icon";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { RiskBadge, type RiskLevel } from "@/components/hifi/RiskBadge";
import { Kbd, Label } from "@/components/hifi/misc";
import { ShellLayout } from "@/components/shell/ShellLayout";

const STEPS: Array<{
  n: number;
  label: string;
  kicker: string;
}> = [
  { n: 1, label: "Scope", kicker: "Identify & scope" },
  { n: 2, label: "Threats", kicker: "3 A's · DBT" },
  { n: 3, label: "Likelihood", kicker: "1–5 scale" },
  { n: 4, label: "Impact", kicker: "5 dimensions" },
  { n: 5, label: "Inherent Risk", kicker: "IRV · Matrix" },
  { n: 6, label: "Vulnerability", kicker: "Controls" },
  { n: 7, label: "Treatment", kicker: "Review · priority" },
];

const ADVERSARY_TYPES = [
  "CRIMINAL",
  "TERRORIST",
  "INSIDER",
  "ACTIVIST",
  "NATION_STATE",
  "NATURAL",
  "ACCIDENTAL",
] as const;

const ACTION_TYPES = [
  "THEFT",
  "SABOTAGE",
  "ATTACK",
  "VANDALISM",
  "ESPIONAGE",
  "INTRUSION",
  "DISRUPTION",
  "OTHER",
] as const;

const VULN_RATINGS = ["STRONG", "BASELINE", "BARELY_ADEQUATE", "INADEQUATE"] as const;

const IMPACT_DIMENSIONS: Array<{ key: keyof ImpactBreakdown; label: string; abbr: string }> = [
  { key: "people", label: "People", abbr: "PPL" },
  { key: "property", label: "Property", abbr: "PRP" },
  { key: "operations", label: "Operations", abbr: "OPS" },
  { key: "reputation", label: "Reputation", abbr: "REP" },
  { key: "financial", label: "Financial", abbr: "FIN" },
];

function formatError(err: unknown, fallback: string): string {
  if (err instanceof HTTPError) {
    return `${fallback} (${String(err.response.status)})`;
  }
  return fallback;
}

function levelFromValue(v: number | null | undefined): RiskLevel {
  if (!v) return "Negligible";
  if (v >= 5) return "Extreme";
  if (v >= 4) return "High";
  if (v >= 3) return "Moderate";
  if (v >= 2) return "Low";
  return "Negligible";
}

function irvToLevel(irv: string | null): RiskLevel {
  if (!irv) return "Negligible";
  const v = irv.toUpperCase();
  if (v.includes("EXTREME") || v.includes("HIGHEST")) return "Extreme";
  if (v.includes("HIGH")) return "High";
  if (v.includes("MOD") || v.includes("MEDIUM")) return "Moderate";
  if (v.includes("LOW")) return "Low";
  return "Negligible";
}

function stepProgress(step: number, current: number): number {
  if (step < current) return 1;
  if (step === current) return 0.5;
  return 0;
}

export function AssessmentWizardPage(): ReactNode {
  const { assessmentId } = useParams({
    from: "/assessments/$assessmentId/wizard",
  });
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [threats, setThreats] = useState<Threat[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activeThreatId, setActiveThreatId] = useState<string | null>(null);
  const [viewStep, setViewStep] = useState<number | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [commentsCollapsed, setCommentsCollapsed] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [a, ts, as] = await Promise.all([
        getAssessment(assessmentId),
        listThreats(assessmentId),
        listAssets({ limit: 500 }),
      ]);
      setAssessment(a);
      setThreats(ts);
      setAssets(as);
      if (ts.length > 0 && !activeThreatId) {
        const firstId = ts[0]?.id;
        if (firstId) setActiveThreatId(firstId);
      }
    } catch (e) {
      setError(formatError(e, "Load failed"));
    } finally {
      setLoading(false);
    }
  }, [assessmentId, activeThreatId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeThreat = useMemo(
    () => threats.find((t) => t.id === activeThreatId) ?? null,
    [threats, activeThreatId],
  );

  async function wrap<T>(fn: () => Promise<T>, failMsg: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(formatError(e, failMsg));
    } finally {
      setBusy(false);
    }
  }

  if (loading && !assessment) {
    return (
      <ShellLayout
        active="assessments"
        topbar={{ title: "Loading assessment…" }}
      >
        <div className="p-6 text-center text-n-500">Loading…</div>
      </ShellLayout>
    );
  }

  if (!assessment) {
    return (
      <ShellLayout active="assessments" topbar={{ title: "Not found" }}>
        <div className="p-6">
          <div className="rounded-3 border border-bad-bg bg-bad-bg p-4 text-sm text-bad">
            {error ?? "Assessment not found."}
          </div>
          <Btn2
            variant="default"
            size="sm"
            icon="chevLeft"
            className="mt-3"
            onClick={() => void navigate({ to: "/assessments" })}
          >
            Back to assessments
          </Btn2>
        </div>
      </ShellLayout>
    );
  }

  const currentStep = assessment.current_step;
  const displayStep = viewStep ?? currentStep;
  const inReview = assessment.status === "REVIEW";
  const terminal = assessment.status === "APPROVED";
  const scoredCount = threats.filter((t) => t.likelihood_score != null).length;
  const impactScored = threats.filter((t) => t.impact_score != null).length;

  return (
    <ShellLayout
      active="assessments"
      topbar={{
        crumbs: ["Assessments", assessment.id.slice(0, 8)],
        title: assessment.title,
        subtitle: (
          <span className="inline-flex items-center gap-2">
            <Pill>
              {assessment.status.replace("_", " ").toLowerCase()}
            </Pill>
            <span className="text-n-400">·</span>
            <span>{assessment.assessment_type}</span>
            <span className="text-n-400">·</span>
            <span className="font-mono text-[11px]">
              step {currentStep}/7
            </span>
          </span>
        ),
        right: (
          <>
            <Btn2 variant="ghost" size="sm" icon="history">
              History
            </Btn2>
            <Btn2 variant="ghost" size="sm" icon="comment">
              Comments
            </Btn2>
            <Btn2 variant="ghost" size="sm" icon="download">
              Export
            </Btn2>
            <div className="mx-1 h-5 w-px bg-n-200" />
            {!terminal && !inReview && currentStep === 7 && (
              <Btn2
                variant="primary"
                size="sm"
                iconRight="arrRight"
                disabled={busy}
                onClick={() =>
                  void wrap(
                    () => advanceAssessment(assessmentId, 8),
                    "Submit failed",
                  )
                }
              >
                Submit for review
              </Btn2>
            )}
            {!terminal && !inReview && currentStep < 7 && (
              <Btn2
                variant="primary"
                size="sm"
                iconRight="arrRight"
                disabled={busy}
                onClick={() =>
                  void wrap(
                    () => advanceAssessment(assessmentId),
                    "Advance failed",
                  )
                }
              >
                Advance to step {currentStep + 1}
              </Btn2>
            )}
          </>
        ),
      }}
    >
      {/* Step tabs */}
      <div className="flex border-b border-n-150 bg-white px-3">
        {STEPS.map((s) => {
          const on = s.n === displayStep;
          const done = s.n < currentStep;
          const pct = stepProgress(s.n, currentStep) * 100;
          return (
            <button
              key={s.n}
              type="button"
              onClick={() => setViewStep(s.n)}
              className={`relative flex-1 cursor-pointer px-3 pt-2 ${s.n < 7 ? "border-r border-n-100" : ""}`}
            >
              <div className="mb-1.5 flex items-center gap-[7px]">
                <span
                  className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-1 font-mono text-[10px] font-semibold ${
                    done
                      ? "bg-ok text-white"
                      : on
                        ? "bg-a-500 text-white"
                        : "bg-n-100 text-n-500"
                  }`}
                >
                  {done ? <Icon name="check" size={10} /> : s.n}
                </span>
                <div className="min-w-0 flex-1 text-left">
                  <div
                    className={`truncate text-[12px] tracking-[-0.1px] ${
                      on
                        ? "font-semibold text-n-900"
                        : done
                          ? "font-medium text-n-700"
                          : "font-medium text-n-500"
                    }`}
                  >
                    {s.label}
                  </div>
                  <div className="mt-px truncate font-mono text-[9px] text-n-400">
                    {s.kicker}
                  </div>
                </div>
              </div>
              <div className="-mb-px h-0.5 bg-n-100">
                <div
                  className="h-full"
                  style={{
                    width: `${pct}%`,
                    background: on
                      ? "var(--a-500)"
                      : done
                        ? "var(--ok)"
                        : "transparent",
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mx-5 mt-3 rounded-3 border border-bad-bg bg-bad-bg px-3 py-2 text-[12px] text-bad">
          {error}
        </div>
      )}

      {/* Body: threats | form | comments */}
      <div
        className="grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: `320px 1fr ${commentsCollapsed ? "44px" : "320px"}`,
        }}
      >
        {/* Threats list */}
        <div className="flex min-h-0 flex-col border-r border-n-150 bg-white">
          <div className="flex items-center gap-2 border-b border-n-150 px-3.5 py-2.5">
            <h3 className="m-0 text-[13px] font-semibold text-n-900">
              Threats · step {displayStep}
            </h3>
            {scoredCount < threats.length && (
              <Pill variant="warn">
                {threats.length - scoredCount} pending
              </Pill>
            )}
            <div className="flex-1" />
            <IconBtn name="filter" size="sm" />
            <IconBtn name="sort" size="sm" />
          </div>
          <div className="flex items-center gap-1.5 border-b border-n-150 bg-n-50 px-3.5 py-2">
            <Icon name="kbd" size={12} color="var(--n-400)" />
            <span className="text-[11px] text-n-500">
              Sweep mode · <Kbd>J</Kbd> <Kbd>K</Kbd> to navigate
            </span>
          </div>
          <div className="flex-1 overflow-auto">
            {threats.length === 0 && (
              <div className="p-6 text-center text-[12px] text-n-500">
                No threats yet. Add one in step 2.
              </div>
            )}
            {threats.map((t) => {
              const sel = t.id === activeThreatId;
              const assetName =
                assets.find((a) => a.id === t.target_asset_id)?.name ??
                t.target_asset_id.slice(0, 8);
              const imp = t.impact_score;
              return (
                <div
                  key={t.id}
                  onClick={() => setActiveThreatId(t.id)}
                  className={`cursor-pointer border-b border-n-100 border-l-[3px] px-3.5 py-2.5 ${sel ? "border-l-a-500 bg-a-50" : "border-l-transparent bg-transparent hover:bg-n-50"}`}
                >
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <span
                      className={`font-mono text-[10px] font-semibold ${sel ? "text-a-700" : "text-n-500"}`}
                    >
                      {t.id.slice(0, 8)}
                    </span>
                    <div className="flex-1" />
                    {t.irv ? (
                      <RiskBadge level={irvToLevel(t.irv)} size="sm" />
                    ) : (
                      <Pill variant="outline">pending</Pill>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-[22px] w-7 items-center justify-center rounded-1 border border-n-200 font-mono text-[12px] font-bold ${imp ? "bg-n-75 text-n-900" : "bg-n-100 text-n-400"}`}
                    >
                      {imp ?? "—"}
                    </div>
                    <span
                      className={`text-[12.5px] tracking-[-0.1px] text-n-900 ${sel ? "font-semibold" : "font-medium"}`}
                    >
                      {t.adversary_type.toLowerCase()} → {t.action_type.toLowerCase()}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-n-500">
                    on {assetName}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-1.5 border-t border-n-150 bg-n-50 px-3.5 py-2.5">
            {!terminal && !inReview && (
              <CreateThreatPopover
                assets={assets}
                onCreate={async (p) => {
                  await wrap(
                    () => createThreat(assessmentId, p),
                    "Create threat failed",
                  );
                }}
                disabled={busy}
              />
            )}
          </div>
        </div>

        {/* Active form */}
        <div className="flex min-h-0 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-4 py-3.5">
            {activeThreat ? (
              <>
                <ThreatContextCard threat={activeThreat} assets={assets} />
                <StepBody
                  step={displayStep}
                  threat={activeThreat}
                  assessment={assessment}
                  threats={threats}
                  busy={busy}
                  terminal={terminal}
                  inReview={inReview}
                  reviewNotes={reviewNotes}
                  setReviewNotes={setReviewNotes}
                  onScoreLikelihood={async (score) => {
                    await wrap(
                      () =>
                        scoreLikelihood(
                          assessmentId,
                          activeThreat.id,
                          score,
                        ),
                      "Likelihood failed",
                    );
                  }}
                  onScoreImpact={async (breakdown) => {
                    await wrap(
                      () =>
                        scoreImpact(assessmentId, activeThreat.id, breakdown),
                      "Impact failed",
                    );
                  }}
                  onScoreVulnerability={async (rating) => {
                    await wrap(
                      () =>
                        scoreVulnerability(
                          assessmentId,
                          activeThreat.id,
                          rating,
                        ),
                      "Vulnerability failed",
                    );
                  }}
                  onDeleteThreat={async () => {
                    await wrap(
                      () => deleteThreat(assessmentId, activeThreat.id),
                      "Delete failed",
                    );
                  }}
                  onReview={async (action) => {
                    await wrap(
                      () =>
                        reviewAssessment(
                          assessmentId,
                          action,
                          reviewNotes || null,
                        ),
                      "Review failed",
                    );
                  }}
                />
              </>
            ) : (
              <Card className="p-8 text-center text-n-500">
                Add a threat to begin. Step 1 asks you to scope the assessment;
                step 2 onwards operates on individual threats.
              </Card>
            )}
          </div>

          {/* Footer nav */}
          <div className="flex flex-shrink-0 items-center gap-2 border-t border-n-150 bg-white px-4 py-2">
            <Btn2
              variant="default"
              size="sm"
              icon="chevLeft"
              disabled={displayStep <= 1}
              onClick={() => setViewStep(Math.max(1, displayStep - 1))}
            >
              Prev
            </Btn2>
            <Btn2
              variant="default"
              size="sm"
              iconRight="chevRight"
              disabled={displayStep >= 7}
              onClick={() => setViewStep(Math.min(7, displayStep + 1))}
            >
              Next
            </Btn2>
            <span className="ml-1 text-[11px] text-n-500">
              {impactScored}/{threats.length} scored
            </span>
            <div className="mx-2 h-1 max-w-[160px] flex-1 overflow-hidden rounded-full bg-n-100">
              <div
                className="h-full bg-a-500"
                style={{
                  width: `${threats.length > 0 ? (impactScored / threats.length) * 100 : 0}%`,
                }}
              />
            </div>
            <div className="flex-1" />
            {!terminal && !inReview && currentStep === 7 && (
              <Btn2
                variant="primary"
                size="sm"
                iconRight="arrRight"
                disabled={busy}
                onClick={() =>
                  void wrap(
                    () => advanceAssessment(assessmentId, 8),
                    "Submit failed",
                  )
                }
              >
                Submit for review
              </Btn2>
            )}
            {!terminal && !inReview && currentStep < 7 && (
              <Btn2
                variant="primary"
                size="sm"
                iconRight="arrRight"
                disabled={busy}
                onClick={() =>
                  void wrap(
                    () => advanceAssessment(assessmentId),
                    "Advance failed",
                  )
                }
              >
                Complete step {currentStep}
              </Btn2>
            )}
          </div>
        </div>

        {/* Comments placeholder */}
        <CommentsPanelStub
          collapsed={commentsCollapsed}
          onToggle={() => setCommentsCollapsed((v) => !v)}
        />
      </div>
    </ShellLayout>
  );
}

function ThreatContextCard({
  threat,
  assets,
}: {
  threat: Threat;
  assets: Asset[];
}): ReactNode {
  const asset = assets.find((a) => a.id === threat.target_asset_id);
  return (
    <Card style={{ padding: "10px 14px" }}>
      <div className="mb-2 flex flex-wrap items-center gap-2.5">
        <span className="font-mono text-[10px] font-semibold text-a-600">
          {threat.id.slice(0, 8)}
        </span>
        <Pill variant="outline">{threat.adversary_type}</Pill>
        <Pill variant="outline">{threat.action_type}</Pill>
        <div className="flex-1" />
        <Label className="!m-0">Current IRV</Label>
        <span className="font-mono text-[10px] text-n-500">
          L:{threat.likelihood_score ?? "—"} × I:{threat.impact_score ?? "—"}
        </span>
        {threat.irv && <RiskBadge level={irvToLevel(threat.irv)} size="sm" />}
      </div>
      <h2 className="mb-2 text-[15px] font-semibold tracking-[-0.3px] text-n-900">
        {threat.adversary_type.toLowerCase()} — {threat.action_type.toLowerCase()}
      </h2>
      <div
        className="grid gap-2.5 border-t border-dashed border-n-150 pt-2"
        style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
      >
        <ThreeA
          label="Adversary"
          value={threat.adversary_type}
          sub={threat.description ?? "generic profile"}
        />
        <ThreeA
          label="Asset"
          value={asset?.name ?? threat.target_asset_id.slice(0, 8)}
          sub={asset?.asset_type ?? "asset"}
        />
        <ThreeA
          label="Action"
          value={threat.action_type}
          sub={threat.vulnerability_rating ?? "vuln tbd"}
        />
      </div>
    </Card>
  );
}

function ThreeA({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): ReactNode {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 text-[13px] font-medium tracking-[-0.1px] text-n-900">
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 font-mono text-[10px] text-n-500">{sub}</div>
      )}
    </div>
  );
}

interface StepBodyProps {
  step: number;
  threat: Threat;
  assessment: Assessment;
  threats: Threat[];
  busy: boolean;
  terminal: boolean;
  inReview: boolean;
  reviewNotes: string;
  setReviewNotes: (s: string) => void;
  onScoreLikelihood: (score: number) => Promise<void>;
  onScoreImpact: (breakdown: ImpactBreakdown) => Promise<void>;
  onScoreVulnerability: (rating: string) => Promise<void>;
  onDeleteThreat: () => Promise<void>;
  onReview: (action: "approve" | "reject") => Promise<void>;
}

function StepBody(props: StepBodyProps): ReactNode {
  const { step } = props;
  if (step === 1) return <Step1Scope {...props} />;
  if (step === 2) return <Step2Threat {...props} />;
  if (step === 3) return <Step3Likelihood {...props} />;
  if (step === 4) return <Step4Impact {...props} />;
  if (step === 5) return <Step5IRV {...props} />;
  if (step === 6) return <Step6Vulnerability {...props} />;
  return <Step7Treatment {...props} />;
}

function Step1Scope({ assessment, threats }: StepBodyProps): ReactNode {
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 1 · Scope"
        pills={<Pill variant="accent">read-only</Pill>}
        right={
          <span className="text-[11px] text-n-500">
            Target assets locked at assessment creation
          </span>
        }
      />
      <div className="space-y-2 p-3.5 text-[12.5px] text-n-700">
        <div>
          <Label>Assessment type</Label>
          <div className="text-n-900">{assessment.assessment_type}</div>
        </div>
        <div>
          <Label>Scope</Label>
          <div className="text-n-900">
            {assessment.scope_asset_ids.length > 0
              ? `${assessment.scope_asset_ids.length} scoped assets`
              : "Single asset"}
          </div>
        </div>
        <div>
          <Label>Compliance tags</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {assessment.compliance_tags.length > 0 ? (
              assessment.compliance_tags.map((t) => (
                <Pill key={t} variant="accent">
                  {t}
                </Pill>
              ))
            ) : (
              <span className="text-n-500">none</span>
            )}
          </div>
        </div>
        <div>
          <Label>Threats identified</Label>
          <div className="text-n-900">{threats.length}</div>
        </div>
      </div>
    </Card>
  );
}

function Step2Threat({
  threat,
  busy,
  terminal,
  inReview,
  onDeleteThreat,
}: StepBodyProps): ReactNode {
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 2 · Threat definition"
        pills={<Pill variant="accent">active</Pill>}
        right={
          !terminal &&
          !inReview && (
            <Btn2
              variant="danger"
              size="xs"
              icon="x"
              disabled={busy}
              onClick={() => {
                if (window.confirm("Delete this threat?")) void onDeleteThreat();
              }}
            >
              Delete
            </Btn2>
          )
        }
      />
      <div className="space-y-3 p-3.5 text-[12.5px] text-n-700">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Adversary type</Label>
            <div className="mt-0.5 text-n-900">{threat.adversary_type}</div>
          </div>
          <div>
            <Label>Action type</Label>
            <div className="mt-0.5 text-n-900">{threat.action_type}</div>
          </div>
        </div>
        {threat.description && (
          <div>
            <Label>Description</Label>
            <div className="mt-0.5 text-n-900">{threat.description}</div>
          </div>
        )}
        <div className="border-t border-n-100 pt-2 text-[11px] text-n-500">
          Created {new Date(threat.created_at).toLocaleDateString()} · updated{" "}
          {new Date(threat.updated_at).toLocaleDateString()}
        </div>
      </div>
    </Card>
  );
}

function Step3Likelihood({
  threat,
  busy,
  terminal,
  inReview,
  onScoreLikelihood,
}: StepBodyProps): ReactNode {
  const [value, setValue] = useState<number>(threat.likelihood_score ?? 3);
  useEffect(() => {
    setValue(threat.likelihood_score ?? 3);
  }, [threat.id, threat.likelihood_score]);
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 3 · Likelihood"
        pills={<Pill variant="accent">active</Pill>}
        right={
          <span className="text-[11px] text-n-500">
            1 rare · 3 moderate · 5 almost certain
          </span>
        }
      />
      <div className="p-3.5">
        <div className="mb-3 flex items-baseline gap-2">
          <Label className="!m-0">Current</Label>
          <span className="text-[26px] font-semibold leading-none tracking-[-1px] text-n-900">
            {threat.likelihood_score ?? "—"}
          </span>
          {threat.likelihood_score != null && (
            <RiskBadge level={levelFromValue(threat.likelihood_score)} size="sm" />
          )}
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => {
            const on = n === value;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setValue(n)}
                className={`h-9 flex-1 rounded-2 border text-[13px] font-semibold ${on ? "border-a-600 bg-a-500 text-white shadow-1" : "border-n-200 bg-white text-n-700 hover:bg-n-50"}`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Btn2
            variant="primary"
            size="sm"
            disabled={busy || terminal || inReview}
            onClick={() => void onScoreLikelihood(value)}
          >
            Save likelihood
          </Btn2>
          <span className="text-[11px] text-n-500">
            Saving updates IRV once impact is scored.
          </span>
        </div>
      </div>
    </Card>
  );
}

function Step4Impact({
  threat,
  busy,
  terminal,
  inReview,
  onScoreImpact,
}: StepBodyProps): ReactNode {
  const [breakdown, setBreakdown] = useState<ImpactBreakdown>(() => ({
    people: threat.impact_breakdown.people ?? 3,
    property: threat.impact_breakdown.property ?? 3,
    operations: threat.impact_breakdown.operations ?? 3,
    reputation: threat.impact_breakdown.reputation ?? 3,
    financial: threat.impact_breakdown.financial ?? 3,
  }));
  useEffect(() => {
    setBreakdown({
      people: threat.impact_breakdown.people ?? 3,
      property: threat.impact_breakdown.property ?? 3,
      operations: threat.impact_breakdown.operations ?? 3,
      reputation: threat.impact_breakdown.reputation ?? 3,
      financial: threat.impact_breakdown.financial ?? 3,
    });
  }, [threat.id, threat.impact_breakdown]);

  const maxVal = Math.max(
    ...IMPACT_DIMENSIONS.map((d) => breakdown[d.key] ?? 1),
  );

  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 4 · Impact scoring"
        pills={<Pill variant="accent">active</Pill>}
        right={
          <span className="text-[11px] text-n-500">
            Final = max across 5 dimensions
          </span>
        }
      />
      <div className="flex items-center gap-4 border-b border-n-100 px-3.5 py-3">
        <div>
          <Label>Computed</Label>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <div className="text-[26px] font-semibold leading-none tracking-[-1px] text-n-900">
              {maxVal}
            </div>
            <div className="text-[11px] text-n-500">
              {levelFromValue(maxVal)}
            </div>
          </div>
        </div>
        <div className="h-10 w-px bg-n-150" />
        <div className="flex-1">
          <Label>Distribution</Label>
          <div
            className="mt-1 grid items-end gap-1.5"
            style={{
              gridTemplateColumns: `repeat(${IMPACT_DIMENSIONS.length}, 1fr)`,
            }}
          >
            {IMPACT_DIMENSIONS.map((d) => {
              const v = breakdown[d.key] ?? 1;
              const isMax = v === maxVal;
              const lvl = levelFromValue(v);
              return (
                <div key={d.key} className="flex flex-col items-stretch gap-0.5">
                  <div className="flex items-baseline justify-between gap-1">
                    <span
                      className={`font-mono text-[9px] tracking-[0.3px] ${isMax ? "font-bold text-n-800" : "font-medium text-n-500"}`}
                    >
                      {d.abbr}
                    </span>
                    <span
                      className={`font-mono text-[10px] font-semibold ${isMax ? "text-n-900" : "text-n-600"}`}
                    >
                      {v}
                    </span>
                  </div>
                  <div
                    className="h-2 w-full rounded-1 border"
                    style={{
                      background: `var(--r-${levelVar(lvl)})`,
                      borderColor: isMax ? "var(--n-300)" : "var(--n-200)",
                      boxShadow: isMax ? "0 0 0 1px var(--a-500)" : "none",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div>
        {IMPACT_DIMENSIONS.map((d) => (
          <ImpactRow
            key={d.key}
            label={d.label}
            value={breakdown[d.key] ?? 3}
            onChange={(v) => setBreakdown((b) => ({ ...b, [d.key]: v }))}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 border-t border-n-150 bg-n-50 px-3.5 py-2">
        <Icon name="clock" size={11} color="var(--n-400)" />
        <span className="text-[11px] text-n-500">
          Updates IRV once saved
        </span>
        <div className="flex-1" />
        <Btn2
          variant="primary"
          size="sm"
          disabled={busy || terminal || inReview}
          onClick={() => void onScoreImpact(breakdown)}
        >
          Save impact
        </Btn2>
      </div>
    </Card>
  );
}

function levelVar(level: RiskLevel): string {
  if (level === "Extreme" || level === "Highest") return "ext";
  if (level === "High") return "high";
  if (level === "Moderate" || level === "Medium") return "mod";
  if (level === "Low") return "low";
  return "neg";
}

function ImpactRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): ReactNode {
  return (
    <div
      className="border-b border-n-100 px-3.5 py-2"
      style={{
        display: "grid",
        gridTemplateColumns: "110px 1fr 80px",
        gap: 10,
        alignItems: "center",
      }}
    >
      <div className="text-[12.5px] font-semibold text-n-900">{label}</div>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => {
          const on = n === value;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`h-6 flex-1 rounded-2 border text-[11.5px] font-semibold ${on ? "border-a-600 bg-a-500 text-white shadow-1" : "border-n-200 bg-white text-n-700 hover:bg-n-50"}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <RiskBadge level={levelFromValue(value)} size="sm" />
    </div>
  );
}

function Step5IRV({ threat, threats }: StepBodyProps): ReactNode {
  const scored = threats.filter(
    (t) => t.likelihood_score != null && t.impact_score != null,
  );
  const avg =
    scored.length > 0
      ? scored.reduce((a, t) => a + (t.likelihood_score ?? 0) * (t.impact_score ?? 0), 0) /
        scored.length
      : 0;
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 5 · Inherent Risk Value"
        pills={<Pill variant="accent">computed</Pill>}
        right={
          <span className="text-[11px] text-n-500">IRV = L × I, clamped to band</span>
        }
      />
      <div className="p-3.5">
        <div className="mb-3 flex items-baseline gap-3">
          <div>
            <Label>This threat</Label>
            <div className="mt-0.5 text-[22px] font-semibold leading-none tracking-[-0.8px] text-n-900">
              {threat.irv ?? "—"}
            </div>
          </div>
          {threat.irv && (
            <RiskBadge level={irvToLevel(threat.irv)} size="lg" />
          )}
        </div>
        <div className="text-[12px] text-n-600">
          Likelihood <b>{threat.likelihood_score ?? "—"}</b> × Impact{" "}
          <b>{threat.impact_score ?? "—"}</b>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-[12px]">
          <div className="rounded-2 border border-n-200 bg-white p-2">
            <Label>Avg IRV (assessment)</Label>
            <div className="mt-0.5 font-semibold text-n-900">{avg.toFixed(1)}</div>
          </div>
          <div className="rounded-2 border border-n-200 bg-white p-2">
            <Label>Scored threats</Label>
            <div className="mt-0.5 font-semibold text-n-900">
              {scored.length} / {threats.length}
            </div>
          </div>
          <div className="rounded-2 border border-n-200 bg-white p-2">
            <Label>Extreme count</Label>
            <div className="mt-0.5 font-semibold text-n-900">
              {threats.filter((t) => irvToLevel(t.irv) === "Extreme").length}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Step6Vulnerability({
  threat,
  busy,
  terminal,
  inReview,
  onScoreVulnerability,
}: StepBodyProps): ReactNode {
  const [rating, setRating] = useState<string>(
    threat.vulnerability_rating ?? "BASELINE",
  );
  useEffect(() => {
    setRating(threat.vulnerability_rating ?? "BASELINE");
  }, [threat.id, threat.vulnerability_rating]);
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Step 6 · Vulnerability rating"
        pills={<Pill variant="accent">active</Pill>}
        right={
          <span className="text-[11px] text-n-500">
            Controls assessment × IRV → priority
          </span>
        }
      />
      <div className="p-3.5">
        <Label>Current</Label>
        <div className="mt-0.5 text-[14px] font-semibold text-n-900">
          {threat.vulnerability_rating ?? "—"}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {VULN_RATINGS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setRating(v)}
              className={`rounded-2 border px-3 py-2 text-left text-[12.5px] font-medium transition ${rating === v ? "border-a-600 bg-a-50 text-a-700 shadow-1" : "border-n-200 bg-white text-n-700 hover:bg-n-50"}`}
            >
              <div className="font-semibold">{v.replace("_", " ")}</div>
              <div className="mt-0.5 text-[11px] text-n-500">
                {v === "STRONG"
                  ? "Multiple layered controls, tested and effective"
                  : v === "BASELINE"
                    ? "Standard controls, meets policy"
                    : v === "BARELY_ADEQUATE"
                      ? "Gaps present, controls partially effective"
                      : "Missing or ineffective controls"}
              </div>
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Btn2
            variant="primary"
            size="sm"
            disabled={busy || terminal || inReview || threat.irv == null}
            onClick={() => void onScoreVulnerability(rating)}
          >
            Save vulnerability
          </Btn2>
          {threat.irv == null && (
            <span className="text-[11px] text-warn">
              Score likelihood and impact first.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function Step7Treatment({
  threat,
  threats,
  assessment,
  inReview,
  reviewNotes,
  setReviewNotes,
  busy,
  onReview,
}: StepBodyProps): ReactNode {
  return (
    <div className="flex flex-col gap-3">
      <Card className="flex flex-col">
        <CardHeader
          title="Step 7 · Treatment priority"
          pills={
            threat.risk_treatment_priority ? (
              <Pill variant="accent">
                {threat.risk_treatment_priority}
              </Pill>
            ) : undefined
          }
        />
        <div className="p-3.5 text-[12.5px] text-n-700">
          <div className="flex items-baseline gap-3">
            <div>
              <Label>IRV</Label>
              <div className="mt-0.5 text-[18px] font-semibold text-n-900">
                {threat.irv ?? "—"}
              </div>
            </div>
            <div>
              <Label>Vulnerability</Label>
              <div className="mt-0.5 text-[18px] font-semibold text-n-900">
                {threat.vulnerability_rating ?? "—"}
              </div>
            </div>
            <div>
              <Label>Priority</Label>
              <div className="mt-0.5 text-[18px] font-semibold text-n-900">
                {threat.risk_treatment_priority ?? "—"}
              </div>
            </div>
          </div>
          <div className="mt-3 border-t border-n-100 pt-2 text-[11px] text-n-500">
            Action plans for this threat are managed on the Action Plans page.
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Assessment totals"
          pills={<Pill>{threats.length} threats</Pill>}
        />
        <div className="grid grid-cols-5 gap-0 border-t border-n-100">
          {(["Negligible", "Low", "Moderate", "High", "Extreme"] as RiskLevel[]).map(
            (lvl) => {
              const count = threats.filter((t) => irvToLevel(t.irv) === lvl).length;
              return (
                <div
                  key={lvl}
                  className="flex flex-col items-center justify-center border-r border-n-100 px-2 py-3 last:border-r-0"
                  style={{ background: `var(--r-${levelVar(lvl)})` }}
                >
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-wide">
                    {lvl}
                  </div>
                  <div className="mt-1 text-[22px] font-semibold leading-none text-n-900">
                    {count}
                  </div>
                </div>
              );
            },
          )}
        </div>
      </Card>

      {inReview && (
        <Card>
          <CardHeader title="Review decision" />
          <div className="p-3.5">
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Review notes (optional)"
              className="w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[12.5px]"
              rows={3}
            />
            <div className="mt-3 flex gap-2">
              <Btn2
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() => void onReview("approve")}
              >
                Approve
              </Btn2>
              <Btn2
                variant="danger"
                size="sm"
                disabled={busy}
                onClick={() => void onReview("reject")}
              >
                Reject · back to IN_PROGRESS
              </Btn2>
            </div>
          </div>
        </Card>
      )}

      {assessment.status === "APPROVED" && (
        <Card accent="var(--ok)">
          <div className="p-3.5 text-[12.5px] text-ok">
            Assessment approved · completed {assessment.completed_at ?? "—"}
          </div>
        </Card>
      )}
    </div>
  );
}

function CreateThreatPopover({
  assets,
  onCreate,
  disabled,
}: {
  assets: Asset[];
  onCreate: (p: {
    target_asset_id: string;
    adversary_type: string;
    action_type: string;
  }) => Promise<void>;
  disabled: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>(assets[0]?.id ?? "");
  const [adv, setAdv] = useState<string>("CRIMINAL");
  const [act, setAct] = useState<string>("THEFT");

  useEffect(() => {
    if (!target && assets.length > 0) {
      const id = assets[0]?.id;
      if (id) setTarget(id);
    }
  }, [assets, target]);

  return (
    <div className="relative flex flex-1 gap-1.5">
      <Btn2
        variant="default"
        size="xs"
        icon="plus"
        full
        onClick={() => setOpen((v) => !v)}
      >
        Add threat
      </Btn2>
      <Btn2 variant="default" size="xs" icon="folder">
        DBT
      </Btn2>
      {open && (
        <div className="absolute bottom-full left-0 right-0 z-10 mb-2 rounded-3 border border-n-200 bg-white p-3 shadow-3">
          <div className="flex flex-col gap-2">
            <label className="block text-[11px] text-n-600">
              Target asset
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1 w-full rounded-2 border border-n-200 bg-white px-2 py-1.5 text-[12.5px]"
              >
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-[11px] text-n-600">
                Adversary
                <select
                  value={adv}
                  onChange={(e) => setAdv(e.target.value)}
                  className="mt-1 w-full rounded-2 border border-n-200 bg-white px-2 py-1.5 text-[12.5px]"
                >
                  {ADVERSARY_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] text-n-600">
                Action
                <select
                  value={act}
                  onChange={(e) => setAct(e.target.value)}
                  className="mt-1 w-full rounded-2 border border-n-200 bg-white px-2 py-1.5 text-[12.5px]"
                >
                  {ACTION_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Btn2
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={disabled}
              >
                Cancel
              </Btn2>
              <Btn2
                variant="primary"
                size="sm"
                icon="plus"
                disabled={disabled || !target}
                onClick={async () => {
                  await onCreate({
                    target_asset_id: target,
                    adversary_type: adv,
                    action_type: act,
                  });
                  setOpen(false);
                }}
              >
                Add threat
              </Btn2>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentsPanelStub({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): ReactNode {
  if (collapsed) {
    return (
      <aside className="flex min-h-0 flex-col items-center gap-2.5 border-l border-n-150 bg-white py-2.5">
        <button
          type="button"
          onClick={onToggle}
          title="Expand comments"
          className="flex h-7 w-7 items-center justify-center rounded-1 border border-n-200 bg-white"
        >
          <Icon name="arrLeft" size={14} color="var(--n-700)" />
        </button>
        <Icon name="comment" size={16} color="var(--n-700)" />
        <div
          className="mt-1 font-sans text-[11px] font-semibold uppercase tracking-[0.3px] text-n-600"
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
          }}
        >
          Comments
        </div>
        <div className="flex-1" />
      </aside>
    );
  }
  return (
    <aside className="flex min-h-0 flex-col border-l border-n-150 bg-white">
      <div className="flex items-center gap-2 border-b border-n-150 px-3.5 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          title="Collapse comments"
          className="flex h-[22px] w-[22px] items-center justify-center rounded-1"
        >
          <Icon name="arrRight" size={14} color="var(--n-600)" />
        </button>
        <Icon name="comment" size={14} color="var(--n-700)" />
        <h3 className="m-0 text-[13px] font-semibold text-n-900">Comments</h3>
        <div className="flex-1" />
        <Pill>0</Pill>
      </div>
      <div className="flex-1 overflow-auto p-3.5 text-[12px] text-n-500">
        Commenting is coming in a later release. For now, use the review notes
        in step 7.
      </div>
    </aside>
  );
}
